import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import styles from './fields.module.css';

export interface MatrixDropdownFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const MatrixDropdownField: React.FC<MatrixDropdownFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[element.name];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = Array.isArray(element.rows) ? element.rows : [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: any[] = Array.isArray(element.columns) ? element.columns : [];

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={{}}
        render={({ field }) => {
          const values: Record<string, Record<string, string>> = field.value ||
          {};

          const handleChange = (
            rowValue: string,
            colValue: string,
            selectedValue: string
          ) => {
            field.onChange({
              ...values,
              [rowValue]: {
                ...(values[rowValue] || {}),
                [colValue]: selectedValue,
              },
            });
          };

          return (
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.matrixTable}>
                <thead>
                  <tr>
                    <th aria-label="Linha" />
                    {columns.map((col: any, index: number) => {
                      const colText =
                        typeof col === 'object'
                          ? col.title || col.text || col.name
                          : col;
                      return <th key={index}>{colText}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: any, rowIndex: number) => {
                    const rowValue = typeof row === 'object' ? row.value : row;
                    const rowText = typeof row === 'object' ? row.text : row;
                    return (
                      <tr key={rowIndex}>
                        <td>{rowText}</td>
                        {columns.map((col: any, colIndex: number) => {
                          const colName =
                            typeof col === 'object'
                              ? col.name || col.value
                              : col;
                          const choices =
                            typeof col === 'object' ? col.choices || [] : [];
                          const currentValue =
                            values[rowValue]?.[colName] || '';

                          return (
                            <td key={colIndex}>
                              <select
                                className={`${styles.input} ${styles.select}`}
                                value={currentValue}
                                onChange={(e) =>
                                  handleChange(
                                    rowValue,
                                    colName,
                                    e.target.value
                                  )
                                }
                                disabled={element.readOnly}
                                style={{ minWidth: '120px' }}
                                aria-label={`${rowText} - ${colName}`}
                              >
                                <option value="">Selecione</option>
                                {choices.map(
                                  (choice: any, choiceIndex: number) => {
                                    const choiceValue =
                                      typeof choice === 'object'
                                        ? choice.value
                                        : choice;
                                    const choiceText =
                                      typeof choice === 'object'
                                        ? choice.text
                                        : choice;
                                    return (
                                      <option
                                        key={choiceIndex}
                                        value={choiceValue}
                                      >
                                        {choiceText}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default MatrixDropdownField;
